<template>
  <section v-if="isVisible" id="PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS" class="offset-bottom-2 scroll_top">
    <template v-if="hasShowCCTransactionView && transactionDetails">
      <VTabset @select:tab="onTabSelect">
        <template #label> {{ $t('transactions.details_headline') }} {{ transactionId }} </template>
        <template #default>
          <VTab label="transactions.customer_view_radio_button_label" :active="isCustomerMode" name="customer" />
          <VTab label="transactions.basic_view_radio_button_label" :active="isBasicMode" name="basic" />
          <VTab label="transactions.expert_view_radio_button_label" :active="isExpertMode" name="expert" />
        </template>
      </VTabset>
    </template>
    <div v-else-if="partnerInfo" class="tabset-header">
      <h3>{{ $t('transactions.details_headline_customer') }}</h3>
    </div>

    <transition name="fade" mode="out-in" appear>
      <VMessage v-if="msgBoxLoadTransactionDetail.type" v-bind="msgBoxLoadTransactionDetail" class="offset-top-2" style="margin-bottom: 1500px" />
      <div v-else>
        <div v-if="isCustomerMode" id="partnerInfo" class="row">
          <div class="col-l-6 col-m-12 pull-right text-right">
            <div v-if="entry && entry.hasImage" class="logo_container offset-right-1">
              <img :src="`delegates/barring/content/businesspartner/${entry.partnerNumber}`" alt="" aria-hidden="true" class="logo" />
            </div>
          </div>
          <div class="col-l-6 col-m-12 pull-left">
            <VSection level="3" label="transactions.partner_information">
              <VDescriptionList>
                <VEntry :data="partnerInfo" field="name" name="transactions.partner" />
                <VEntry :data="partnerInfo" field="website" name="transactions.internet" />
                <VEntry :data="partnerInfo" field="phone" name="transactions.hotline" />
                <VEntry :data="partnerInfo" field="email" name="transactions.email" />
                <VEntry name="transactions.address">
                  <VAddress :data="partnerInfo" />
                </VEntry>
              </VDescriptionList>
            </VSection>
          </div>
        </div>

        <div v-if="isCustomerMode" id="serviceInfo" class="row">
          <div class="col-l-6 col-m-12">
            <VSection level="3" label="transactions.overview_service_column_heading">
              <VDescriptionList>
                <VEntry :data="entry" field="serviceDescription" name="transactions.overview_service_column_heading" />
                <VEntry v-slot="{ data }" :data="entry" field name="transactions.overview_price_column_heading">
                  <template v-if="data.retailChargeBooked">
                    {{ formatCurrency(data.retailChargeBooked.gross, data.retailChargeBooked.currency) }}
                  </template>
                  <template v-else-if="data.retailChargeAuthorized">
                    {{ formatCurrency(data.retailChargeAuthorized.gross, data.retailChargeAuthorized.currency) }}
                  </template>
                </VEntry>
                <VEntry :data="entry" field="stateDescription" name="transactions.summary_transaction_status_label" />
                <VEntry :data="entry" field="subscriptionChargeTypeId" name="transactions.transactionType" />
              </VDescriptionList>
            </VSection>
          </div>
        </div>
        <div v-if="!hasShowCCTransactionView" class="notification notification-positive notification notification-dismissible offset-top-2">
          <div class="notification-heading" style="text-align:center">{{ $t('transactions.further_information') }}</div>
        </div>

        <template v-if="!isCustomerMode">
          <VIntro :transaction-details="transactionDetails" />
          <VPrices :transaction-details="transactionDetails" />
          <VProduct :transaction-details="transactionDetails" />
          <VBundle :transaction-details="transactionDetails" />
          <VConfirmations v-if="isExpertMode" :transaction-details="transactionDetails" />
          <VTechnicalDetails :transaction-details="transactionDetails" />
        </template>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import VSection from '@/components/Section';
import VDescriptionList from '@/components/DescriptionList';
import VEntry from '@/components/DescriptionList/Entry';
import VAddress from '@/components/Address';
import Proxy from '@/proxies/PaymentTransactionsProxy';
import FormatCurrencyMixin from '@/mixins/formatCurrency';
import MessageMixin from '@/mixins/message';
import BarringProxy from '@/proxies/BarringProxy';

export default {
  name: 'PaymentTransactionsDetails',

  components: {
    VSection,
    VDescriptionList,
    VEntry,
    VAddress,
    VMessage: () => import('@/components/Message'),
    VTabset: () => import('@/components/UI/Tabs/Tabset'),
    VTab: () => import('@/components/UI/Tabs/Tab'),
    VIntro: () => import('./Intro'),
    VPrices: () => import('./Prices'),
    VProduct: () => import('./Product'),
    VBundle: () => import('./Bundle'),
    VConfirmations: () => import('./Confirmations'),
    VTechnicalDetails: () => import('./TechnicalDetails'),
  },

  mixins: [FormatCurrencyMixin, MessageMixin],

  props: {
    entry: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      transactionId: null,
      transactionDetails: null,
      partnerInfo: null,
      msgBoxLoadTransactionDetail: {
        type: '',
        textKey: '',
      },
    };
  },

  computed: {
    isVisible() {
      return this.$route.params.transactionId;
    },

    ...mapGetters('system', ['hasShowCCTransactionView']),

    ...mapGetters('transactions', ['isCustomerMode', 'isBasicMode', 'isExpertMode']),
  },

  watch: {
    isVisible: {
      handler: 'onTransactionIdChanged',
      immediate: true,
    },

    entry: {
      handler: 'onEntryChanged',
      immediate: true,
    },
  },

  methods: {
    async onEntryChanged(val) {
      this.partnerInfo = null;
      if (!val) return;

      try {
        this.partnerInfo = await new BarringProxy().getPartner(val.partnerNumber);
      } catch (error) {
        // no partner info available
      }
    },

    async onTransactionIdChanged(val) {
      const transactionId = parseInt(val, 10);
      if (transactionId === this.transactionId) return;
      this.transactionId = transactionId;
      this.transactionDetails = null;
      if (!this.transactionId) return;

      if (this.hasShowCCTransactionView) {
        this.setCompMessage(this.msgBoxLoadTransactionDetail, 'LOADING');
        try {
          const response = await new Proxy().find(this.transactionId);
          if (response === null) {
            this.setCompMessage(this.msgBoxLoadTransactionDetail, 'ERROR', 'transactions.msgErrFoundNoData');
            return;
          }
          this.transactionDetails = response;

          this.clearCompMessage(this.msgBoxLoadTransactionDetail);

          this.$emit('update', response);
        } catch (error) {
          this.setCompMessage(this.msgBoxLoadTransactionDetail, 'ERROR', 'errorcodes.BACKEND_ERROR');
        }
      }
    },

    onTabSelect(tab) {
      this.$store.dispatch('transactions/setMode', tab.name);
    },
  },
};
</script>
